import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import { Sparkles, Check, ArrowRight, Layers, Zap, Users, ChevronRight, X } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "Punch List App with Photo Proof | Send-Proof";
const DESC = "Add a punch list to Fieldwire or Procore with Send-Proof: text your crew a link, get a photo on every item, and see when it opens. No login for your crew.";

const faqs = [
  { q: "Do I have to leave Fieldwire or Procore?", a: "No. Keep whatever platform runs your big projects. Send-Proof is the quick punch-list layer you add on top — for the lists you'd otherwise text or scribble on paper." },
  { q: "Do my subs need an account or an app?", a: "No. You send a link by text or email. They tap it, mark items, and snap a photo on each. Nothing to download, no seat to buy." },
  { q: "Why is it so much cheaper?", a: "It's one flat $9.99/month for you. The crew and clients you send lists to never pay or create an account — so the price stays $9.99 no matter how many people you send lists to." },
  { q: "Can I try it on a single job first?", a: "Yes. Start a 15-day free trial with no credit card, run it on one punch list, and see the photo proof come back before you decide." },
];

const rows = [
  { label: "Big multi-month projects, scheduling, RFIs, drawings", platform: true, sp: false, spNote: "Not our job" },
  { label: "A quick punch list you text in 30 seconds", platform: false, sp: true },
  { label: "Subs work it with no app and no account", platform: false, sp: true },
  { label: "Photo proof + real-time open tracking on one list", platform: false, sp: true },
  { label: "Add your whole crew for one flat price", platform: false, sp: true, platNote: "Per seat" },
];

export default function PunchListApp() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");

  // Per-page SEO: title, description, and FAQ structured data (Google runs JS).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-punchlist";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      const el = document.getElementById("ld-punchlist");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[900px]">
        <div className="absolute left-1/2 top-[-200px] h-[520px] w-[860px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        {/* Nav */}
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="pl-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="pl-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        {/* Hero */}
        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-3xl px-5 pt-10 text-center sm:px-8 sm:pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
            <Layers className="h-3.5 w-3.5" /> Works alongside Fieldwire, Procore &amp; a clipboard
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl">
            The 30-second punch list your crew will <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">actually use.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
            Send-Proof is a punch list app: you text your crew a link, get a photo on every item, and see when it opens. Already run Fieldwire or Procore? Keep them — add Send-Proof for the quick punch list, with no new logins for your subs.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button size="lg" className="h-12 w-full bg-emerald-500 px-6 text-base hover:bg-emerald-600 sm:w-auto" data-testid="pl-hero-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <button onClick={() => navigate("/demo")} data-testid="pl-sample-cta" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md border-2 border-emerald-500 px-6 text-base font-semibold text-emerald-500 transition-colors hover:bg-emerald-500/10 sm:w-auto">
              <Sparkles className="h-4 w-4" /> Try the live demo
            </button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Start free — <span className="font-semibold text-foreground">no credit card required</span>. $9.99/mo after your trial.</p>
        </motion.section>

        {/* You don't have to rip anything out */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">You don't have to rip anything out</h2>
          <p className="mt-2 max-w-lg text-base text-muted-foreground">Send-Proof is the simple piece that plays nice with everything you already run.</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {[
              { icon: Layers, t: "Keep your platform", d: "Fieldwire, Procore, spreadsheets — whatever runs your big jobs stays exactly where it is." },
              { icon: Zap, t: "Add the easy part", d: "For a quick punch list, skip the heavy tool. Text a link, get a photo on every item, verify it's done." },
              { icon: Users, t: "Your subs stay happy", d: "No app, no account, no seat to buy. They open a link like any text and get to work." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className={`${panel} p-6`} data-testid={`pl-card-${t}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-base font-bold">{t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Right tool for the job */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-3xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Use the right tool for the job</h2>
          <p className="mt-2 max-w-lg text-base text-muted-foreground">Full platforms are built for big, long jobs. Send-Proof is built for the quick punch list you'd otherwise text.</p>
          <div className={`${panel} mt-8 overflow-hidden`} data-testid="pl-table">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>What you need</span>
              <span className="w-20 text-center">Platforms</span>
              <span className="w-20 text-center text-emerald-400">Send-Proof</span>
            </div>
            {rows.map((r) => (
              <div key={r.label} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-border/60 px-4 py-3 text-sm last:border-0">
                <span className="text-foreground">{r.label}</span>
                <span className="flex w-20 justify-center">
                  {r.platform ? <Check className="h-4 w-4 text-muted-foreground" /> : r.platNote ? <span className="text-[11px] text-muted-foreground">{r.platNote}</span> : <X className="h-4 w-4 text-border" />}
                </span>
                <span className="flex w-20 flex-col items-center justify-center">
                  {r.sp ? <Check className="h-4 w-4 text-emerald-500" /> : r.spNote ? <span className="text-[11px] text-muted-foreground">{r.spNote}</span> : <X className="h-4 w-4 text-border" />}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">No knock on the big platforms — they're great at what they do. This is just the part that should take 30 seconds.</p>
        </motion.section>

        {/* FAQ */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pt-24 sm:px-8" data-testid="pl-faq">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Questions from crews on other tools</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className={`${panel} group p-5`} data-testid="pl-faq-item">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-bold">
                  {f.q}
                  <ChevronRight className="h-5 w-5 shrink-0 text-emerald-500 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pb-24 pt-24 text-center sm:px-8">
          <div className="rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-8 shadow-2xl shadow-emerald-500/20">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Add the easy punch list to your stack</h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">$9.99/month, one flat plan. Try it on one job first — 15 days free, no credit card.</p>
            <Button size="lg" className="mt-6 h-12 bg-emerald-500 px-8 text-base hover:bg-emerald-600" data-testid="pl-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.section>

        {/* Footer */}
        <SiteFooter />
      </div>
    </div>
  );
}
